import { useTranslation } from 'react-i18next';
import { API_URL } from '@/utils/static';
import styled from 'styled-components';
import { Button, Card, Empty, Typography } from 'antd';
import { ProductOutlined } from '@ant-design/icons';
import { BREAKPOINTS, Flex } from '@/ui';
import { STATUS } from '@/utils/static/kyc';

const { Title, Text } = Typography;

export const TableProductItem = ({ product, adminView }) => {
  const { t } = useTranslation();

  const imgSrc = product.logoURL ? `${API_URL}/${product.logoURL}` : '';

  const renderCover = () => {
    if (product.logoURL) {
      return <StyledImage src={imgSrc} alt={product.title || 'Product Image'} />;
    } else {
      return (
        <NoImagePlaceholder>
          <ProductOutlined />
        </NoImagePlaceholder>
      );
    }
  };

  return (
    <StyledCard
      size={window.innerWidth <= BREAKPOINTS.smd ? 'small' : 'default'}
      hoverable
      cover={<CoverContainer>{renderCover()}</CoverContainer>}
    >
      <Flex vertical gap={window.innerWidth <= BREAKPOINTS.smd ? 'small' : 'middle'}>
        <TextIndentTitle level={4}>{product.name}</TextIndentTitle>

        <div>
          <Text strong>{`${t('Investment amount')}:`}</Text>{' '}
          {product.minimalAmount ? product.minimalAmount : '-'}
        </div>

        <div>
          <Text strong>{`${t('Interest rate')}:`}</Text>{' '}
          {product.interestRate ? product.interestRate : '-'}
        </div>

        <div>
          <Text strong>{`${t('Term')}:`}</Text> {product.term ? product.term : '-'}
        </div>

        {product.applicationId && <div>
          <Text strong>{`${t('Application ID')}:`}</Text> {product.applicationId}
        </div>}

        {product.numberContract && <div>
          <Text strong>{`${t('Contract number')}:`}</Text> {product.numberContract}
        </div>}

        <StyledButton variant='filled' color='primary' size='large' onClick={() => product.onClick(product.id)}>
          {t('Open')}
        </StyledButton>

        {adminView && (
          <StyledButton variant='filled' color='orange' size='large' onClick={() => product.onClickEdit(product.id)}>
            {t('Edit')}
          </StyledButton>
        )}

        {product.status === STATUS.NEED_CHECKING && (
          <StyledButton
            variant='filled'
            color='gold'
            size='large'
            onClick={() => product.onClickCheck(product.id)}
          >
            {t('Check')}
          </StyledButton>
        )}
      </Flex>
    </StyledCard>
  );
};

export const ProductList = ({ products, adminView }) => {
  const { t } = useTranslation();

  return (
    <>
      {!!products && products.length > 0 ?
        <StyledFlex gap='large' wrap>
          {products.map((product) => (
            <TableProductItem key={product.id} product={product} adminView={adminView} />
          ))}
        </StyledFlex> :
        <Flex justify='center' $marginTop={20}>
          <Empty description={t('No data')} />
        </Flex>
      }

    </>
  );
};

const StyledCard = styled(Card)`
  width: 350px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out;
  cursor: default;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    max-width: 450px;
    width: 100%;
  }
`;

const CoverContainer = styled.div`
  position: relative;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    padding: 12px;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 20px 20px 0 0;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    height: 180px;
    border-radius: 14px 14px 0 0;
  }
`;

const NoImagePlaceholder = styled.div`
  height: 200px;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 48px;
  color: #999;
  border-radius: 20px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    height: 180px;
    border-radius: 14px;
    font-size: 32px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledFlex = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    justify-content: center;
  }
`;

const TextIndentTitle = styled(Title)`
    width: 300px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;
